// src/components/CSOChat.js (polling version — updated with edit/delete functionality)
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  FaPaperclip,
  FaCheckDouble,
  FaCheck,
  FaClock,
  FaFileDownload,
  FaFileAlt,
  FaTimes,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { MdSend } from "react-icons/md";
import { formatDistanceToNow, isToday, isYesterday } from "date-fns";
import "./Chat.css";
import { BarLoader } from "react-spinners";

const CSOChat = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [currentUser, setCurrentUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [onlineStaffCount, setOnlineStaffCount] = useState(0);
  const [staffTyping, setStaffTyping] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [openMenuFor, setOpenMenuFor] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const pollingRef = useRef({
    messagesInterval: null,
    staffInterval: null,
    typingInterval: null,
  });
  const typingTimeoutRef = useRef(null);
  const messageTrackerRef = useRef(new Set());
  const initialLoadRef = useRef(true);

  // helper to support different /api/users/me shapes
  const getUserId = (user) => user?.userId ?? user?.id ?? null;

  // fetch current user
  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/me`, {
        withCredentials: true,
        timeout: 5000,
      });
      if (res.data) setCurrentUser(res.data);
    } catch (err) {
      console.error("Error fetching user:", err);
      setError(err.response?.data?.message || "Failed to load user data");
    }
  }, [API_URL]);

  // fetch messages once (initial load)
  const fetchMessagesOnce = useCallback(
    async (csoId) => {
      try {
        const res = await axios.get(`${API_URL}/api/chat/cso/${csoId}`, {
          withCredentials: true,
        });
        if (Array.isArray(res.data)) {
          // ensure messages are ordered oldest -> newest so last item is the latest
          const ordered = [...res.data].sort(
            (a, b) =>
              new Date(a.created_at || a.createdAt || a.timestamp) -
              new Date(b.created_at || b.createdAt || b.timestamp)
          );

          // reset tracker and set messages
          messageTrackerRef.current = new Set(ordered.map((m) => m.id));
          setMessages(ordered);

          // scroll to bottom on initial load so user sees latest message
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
            initialLoadRef.current = false;
          }, 50);

          // mark as read for CSO (best-effort)
          try {
            await axios.post(
              `${API_URL}/api/chat/mark-read`,
              {
                csoId,
                actorType: "cso",
                actorId: String(csoId),
              },
              { withCredentials: true }
            );
          } catch (e) {
            // ignore if endpoint not present
          }
        }
      } catch (err) {
        console.error("Failed to fetch messages", err);
        setError("Failed to load messages");
      }
    },
    [API_URL]
  );

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // presence: mark online/offline (best-effort using sendBeacon/fetch/XHR)
  useEffect(() => {
    const uid = getUserId(currentUser);
    if (!uid) return;

    const url = `${API_URL}/api/chat/presence`;
    const payloadObj = {
      senderType: "cso",
      senderId: String(uid),
      status: "online",
    };

    const sendPresence = async (status) => {
      try {
        await axios.post(
          `${API_URL}/api/chat/presence`,
          { senderType: "cso", senderId: String(uid), status },
          { withCredentials: true }
        );
      } catch (e) {
        // ignore
      }
    };

    // notify online
    sendPresence("online");

    const handleBeforeUnload = () => {
      try {
        const payload = JSON.stringify({
          senderType: "cso",
          senderId: String(uid),
          status: "offline",
        });
        const blob = new Blob([payload], { type: "application/json" });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, blob);
        } else if (typeof fetch === "function") {
          fetch(url, {
            method: "POST",
            body: payload,
            keepalive: true,
            headers: { "Content-Type": "application/json" },
          }).catch(() => {});
        } else {
          try {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", url, false);
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.send(payload);
          } catch (e) {}
        }
      } catch (e) {}
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // also attempt to set offline on SPA navigation/unmount
      sendPresence("offline");
    };
  }, [API_URL, currentUser]);

  // start polling for messages + online staff + typing when user is loaded
  useEffect(() => {
    const uid = getUserId(currentUser);
    if (!uid) return;

    // initial fetch
    fetchMessagesOnce(uid);

    // messages polling every 2.5s
    pollingRef.current.messagesInterval = setInterval(() => {
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/api/chat/cso/${uid}`, {
            withCredentials: true,
          });
          if (!Array.isArray(res.data)) return;
          const newOnes = [];
          res.data.forEach((m) => {
            if (!messageTrackerRef.current.has(m.id)) {
              messageTrackerRef.current.add(m.id);
              newOnes.push(m);
            }
          });
          if (newOnes.length > 0) {
            setMessages((prev) => [...prev, ...newOnes]);
            // scroll after appending
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
          }
        } catch (err) {
          // ignore transient errors
        }
      })();
    }, 2500);

    // online staff polling (best-effort)
    pollingRef.current.staffInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/chat/online-staff`, {
          withCredentials: true,
        });
        if (res.data?.count !== undefined) setOnlineStaffCount(res.data.count);
      } catch (err) {
        // ignore if not implemented
      }
    }, 5000);

    // typing status polling: get whether any staff typing for this CSO
    // pollingRef.current.typingInterval = setInterval(async () => {
    //   try {
    //     const res = await axios.get(`${API_URL}/api/chat/typing/${uid}`, {
    //       withCredentials: true,
    //     });
    //     // server should return something like { staffTyping: true/false } or { csoTyping, staffTyping }
    //     setStaffTyping(
    //       Boolean(
    //         res.data?.staffTyping ||
    //           res.data?.isTyping ||
    //           res.data?.staffIsTyping
    //       )
    //     );
    //   } catch (err) {
    //     // ignore if endpoint not present
    //   }
    // }, 1500);

    return () => {
      if (pollingRef.current.messagesInterval)
        clearInterval(pollingRef.current.messagesInterval);
      if (pollingRef.current.staffInterval)
        clearInterval(pollingRef.current.staffInterval);
      // if (pollingRef.current.typingInterval)
      //   clearInterval(pollingRef.current.typingInterval);
      pollingRef.current = {
        messagesInterval: null,
        staffInterval: null,
        typingInterval: null,
      };
    };
  }, [currentUser, fetchMessagesOnce, API_URL]);

  // scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // notify typing to server (best-effort)
  // const notifyTyping = async (isTyping) => {
  //   const userId = getUserId(currentUser);
  //   if (!userId) return;
  //   try {
  //     await axios.post(
  //       `${API_URL}/api/chat/typing`,
  //       { isTyping, userId, senderType: "cso" },
  //       { withCredentials: true }
  //     );
  //   } catch (err) {
  //     // ignore if endpoint not available
  //   }
  // };

  // auto-resize textarea (Telegram-like)
  // const autoResize = () => {
  //   const ta = messageInputRef.current;
  //   if (!ta) return;
  //   ta.style.height = "auto";
  //   const newHeight = Math.min(ta.scrollHeight, 200); // cap height
  //   ta.style.height = `${newHeight}px`;
  // };
// inside CSOChat
const autoResize = () => {
  const textarea = messageInputRef.current;
  if (textarea) {
    textarea.style.height = "auto"; // reset
    textarea.style.height = textarea.scrollHeight + "px"; // set to needed height
  }
};
useEffect(() => {
  autoResize();
}, [newMessage]);

  // handle typing UI + debounce notify
  // const handleTyping = useCallback(
  //   (isTyping) => {
  //     notifyTyping(isTyping);
  //     if (isTyping) {
  //       setStaffTyping(true);
  //       clearTimeout(typingTimeoutRef.current);
  //       typingTimeoutRef.current = setTimeout(() => {
  //         notifyTyping(false);
  //         setStaffTyping(false);
  //       }, 3000);
  //     } else {
  //       notifyTyping(false);
  //       setStaffTyping(false);
  //     }
  //   },
  //   [currentUser]
  // );

  // Handle opening context menu for a message
  const handleOpenMenu = (messageId, e) => {
    e.preventDefault();
    const container = messagesContainerRef.current;
    const el = document.getElementById(`msg-${messageId}`);
    if (!container || !el) {
      setOpenMenuFor(messageId);
      setMenuPos({ top: 10, left: 10 });
      return;
    }
    const contRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = elRect.top - contRect.top + container.scrollTop;
    const left = Math.min(
      Math.max(elRect.left - contRect.left + 10, 8),
      container.clientWidth - 180
    );
    setMenuPos({ top, left });
    setOpenMenuFor(messageId);
  };

  // When Edit selected: put text into input and focus
  const handleStartEdit = (message) => {
    setEditingMessageId(message.id);
    setNewMessage(message.message || "");
    setOpenMenuFor(null);
    // focus input after next tick
    setTimeout(() => {
      messageInputRef.current?.focus();
      autoResize();
    }, 50);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setNewMessage("");
    setTimeout(() => {
      if (messageInputRef.current) messageInputRef.current.style.height = "";
    }, 0);
  };

  // send message with optimistic UI
  const handleSendMessage = async () => {
    // If currently editing a message, perform edit flow
    if (editingMessageId) {
      const trimmed = (newMessage || "").trim();
      if (!trimmed) return;

      // optimistic update + edited highlight
      setMessages((prev) =>
        prev.map((m) =>
          m.id === editingMessageId
            ? {
                ...m,
                message: trimmed,
                is_edited: true,
                wasRecentlyEdited: true,
              }
            : m
        )
      );

      const editingId = editingMessageId;
      setEditingMessageId(null);

      // clear input and reset height
      setNewMessage("");
      setTimeout(() => {
        if (messageInputRef.current) messageInputRef.current.style.height = "";
      }, 0);

      // remove glow after a short time
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === editingId ? { ...m, wasRecentlyEdited: false } : m
          )
        );
      }, 1200);

      try {
        await axios.put(
          `${API_URL}/api/chat/${editingId}`,
          {
            message: trimmed,
            editor_type: "cso",
            editor_id: getUserId(currentUser),
          },
          { withCredentials: true }
        );
      } catch (err) {
        console.error("Failed to save edit", err);
        // refresh messages for robustness
        const uid = getUserId(currentUser);
        if (uid) await fetchMessagesOnce(uid);
      }
      return;
    }

    // normal send new message flow
    if ((!newMessage.trim() && !file) || isSending || !currentUser) return;
    setIsSending(true);
    const tempId = Date.now();
    const userId = getUserId(currentUser);
    const optimisticMsg = {
      id: `temp-${tempId}`,
      tempId,
      sender_type: "cso",
      sender_id: userId,
      sender_name: currentUser.name ?? currentUser?.fullName ?? "CSO",
      recipient_id: 0,
      message: newMessage,
      chat_file: file?.name || null,
      file_size: file?.size || 0, // <-- correct
      created_at: new Date().toISOString(),
      isOptimistic: true,
      status: "sending",
    };

    // append optimistic
    messageTrackerRef.current.add(optimisticMsg.id);
    setMessages((prev) => [...prev, optimisticMsg]);
    setNewMessage("");
    // clear file and preview only after we've appended optimistic message — keep preview until send completes
    if (fileInputRef.current) fileInputRef.current.value = "";
    // handleTyping(false);

    try {
      const formData = new FormData();
      formData.append("sender_type", "cso");
      formData.append("sender_id", userId);
      formData.append("recipient_id", 0);
      formData.append("message", newMessage);
      formData.append("tempId", tempId);
      if (file) formData.append("chat_file", file);

      const res = await axios.post(`${API_URL}/api/chat`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success && res.data.data) {
        const created = res.data.data;
        // replace optimistic message with server message
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticMsg.id ? created : m))
        );
        messageTrackerRef.current.delete(optimisticMsg.id);
        messageTrackerRef.current.add(created.id);

        // after send success, clear local file & preview
        if (filePreviewUrl) {
          URL.revokeObjectURL(filePreviewUrl);
          setFilePreviewUrl(null);
        }
        setFile(null);
      } else {
        // remove optimistic on failure
        setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
        messageTrackerRef.current.delete(optimisticMsg.id);

        // keep preview if send failed so user can retry
        setError("Failed to send message");
      }
    } catch (err) {
      console.error("Failed to send", err);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
      messageTrackerRef.current.delete(optimisticMsg.id);
      setError("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };
  const updateMessagesPreserveScroll = (updater, forceBottom = false) => {
    const container = messagesContainerRef.current;
    if (!container) {
      setMessages((prev) =>
        typeof updater === "function" ? updater(prev) : updater
      );
      return;
    }

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    const wasAtBottom =
      prevScrollHeight - container.clientHeight - prevScrollTop < 40;

    setMessages((prev) =>
      typeof updater === "function" ? updater(prev) : updater
    );

    // adjust after DOM updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!container) return;
        if (wasAtBottom || forceBottom) {
          container.scrollTop = container.scrollHeight;
        } else {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = Math.max(
            0,
            prevScrollTop + (newScrollHeight - prevScrollHeight)
          );
        }
      }, 0);
    });
  };
  const sortMessagesAscending = (arr) => {
    if (!Array.isArray(arr)) return arr;
    return arr
      .slice()
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };
 const handleDeleteMessage = async (messageId) => {
    // if (!window.confirm("Delete this message?")) return;

    // Mark message as removing so CSS can animate it
    updateMessagesPreserveScroll(
      (prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, isRemoving: true } : m)),
      false
    );
    setOpenMenuFor(null);

    const ANIM_MS = 300; // must match CSS transition duration
    // after animation, remove optimistically and call API
    setTimeout(async () => {
      let removed = null;
      updateMessagesPreserveScroll((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        if (idx !== -1) {
          removed = prev[idx];
          const copy = prev.slice();
          copy.splice(idx, 1);
          return copy;
        }
        return prev;
      }, false);

      try {
        await axios.delete(`${API_URL}/api/chat/${messageId}`, {
          withCredentials: true,
          data: { deleter_type: "cso", deleter_id: getUserId(currentUser) },
        });
        // success: nothing else to do
      } catch (err) {
        console.error("Failed to delete message", err);
        // restore (server might have rejected)
        if (removed)
          updateMessagesPreserveScroll(
            (prev) => sortMessagesAscending([...prev, removed]),
            false
          );
      }
    }, ANIM_MS);
  };

  // handle clicking an existing chat file (download/open)
  const handleFileDownload = (chatFileName) => {
    if (!chatFileName) return;
    const url = `${API_URL}/public/chat/${chatFileName}`;
    window.open(url, "_blank", "noopener");
  };

  // file upload selection (from input)
  const handleFileUpload = (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    // revoke previous preview if any
    if (filePreviewUrl) {
      try {
        URL.revokeObjectURL(filePreviewUrl);
      } catch (e) {}
      setFilePreviewUrl(null);
    }
    const url = URL.createObjectURL(f);
    setFile(f);
    setFilePreviewUrl(url);
  };

  const removeFile = () => {
    if (filePreviewUrl) {
      try {
        URL.revokeObjectURL(filePreviewUrl);
      } catch (e) {}
      setFilePreviewUrl(null);
    }
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // key press handler
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    } else if (newMessage.trim()) {
      // handleTyping(true);
    }
  };

  // format and status helpers
  const formatTime = (timestamp) =>
    formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  const getStatusIcon = (message) => {
    if (message.isOptimistic)
      return (
        <>
          <FaClock /> sending...
        </>
      );
    return message.read_at ? <FaCheckDouble /> : <FaCheck />;
  };
  const formatDayLabel = (timestamp) => {
    if (!timestamp) return "";
    const d = new Date(timestamp);
    if (isNaN(d)) return "";
    if (isToday(d)) return "Today";
    if (isYesterday(d)) return "Yesterday";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }); // e.g. August 20, 2025
  };

  // Close menu on outside click or on container scroll
  useEffect(() => {
    const onDocClick = (e) => {
      if (!openMenuFor) return;
      const menuEl = document.querySelector(".message-context-menu");
      if (menuEl && menuEl.contains(e.target)) return;
      setOpenMenuFor(null);
    };
    const onScroll = () => {
      if (openMenuFor) setOpenMenuFor(null);
    };
    document.addEventListener("click", onDocClick);
    messagesContainerRef.current?.addEventListener("scroll", onScroll);
    return () => {
      document.removeEventListener("click", onDocClick);
      messagesContainerRef.current?.removeEventListener("scroll", onScroll);
    };
  }, [openMenuFor]);

  // cleanup on unmount: clear intervals & try offline presence
  useEffect(() => {
    return () => {
      // clear intervals
      if (pollingRef.current.messagesInterval)
        clearInterval(pollingRef.current.messagesInterval);
      if (pollingRef.current.staffInterval)
        clearInterval(pollingRef.current.staffInterval);
      // if (pollingRef.current.typingInterval)
      //   clearInterval(pollingRef.current.typingInterval);
      // if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

      // revoke preview URL if present
      if (filePreviewUrl) {
        try {
          URL.revokeObjectURL(filePreviewUrl);
        } catch (e) {}
      }

      // try to send offline presence for current user
      const uid = getUserId(currentUser);
      if (uid) {
        try {
          const url = `${API_URL}/api/chat/presence`;
          const payload = JSON.stringify({
            senderType: "cso",
            senderId: String(uid),
            status: "offline",
          });
          const blob = new Blob([payload], { type: "application/json" });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(url, blob);
          } else if (typeof fetch === "function") {
            fetch(url, {
              method: "POST",
              body: payload,
              keepalive: true,
              headers: { "Content-Type": "application/json" },
            }).catch(() => {});
          } else {
            try {
              const xhr = new XMLHttpRequest();
              xhr.open("POST", url, false);
              xhr.setRequestHeader("Content-Type", "application/json");
              xhr.send(payload);
            } catch (e) {}
          }
        } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatLocalTime = (timestamp) => {
    try {
      const d = new Date(timestamp);
      if (isNaN(d)) return "";
      return d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      }); // device local time
    } catch (e) {
      return "";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const renderMessagesWithDividers = () => {
    const nodes = [];
    let lastDateKey = null;

    messages.forEach((message) => {
      const ts =
        message.created_at ||
        message.createdAt ||
        message.timestamp ||
        new Date().toISOString();
      const d = ts ? new Date(ts) : new Date();
      const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const myId = getUserId(currentUser);
      const isMy =
        String(message.sender_id) === String(myId) &&
        message.sender_type === "cso";

      if (lastDateKey !== dateKey) {
        nodes.push(
          <div key={`day-${dateKey}`} className="day-divider">
            <span className="divider-text">{formatDayLabel(ts)}</span>
          </div>
        );
        lastDateKey = dateKey;
      }

      const csoMessageRead = Boolean(message.read_at || message.is_read);
      const isOwnCsoMessage =
        message.sender_type === "cso" &&
        String(message.sender_id) === String(getUserId(currentUser));

      // dynamic classes for animation states
      const cls = [
        "message",
        message.sender_type === "cso"
          ? isOwnCsoMessage
            ? "sent"
            : "cso-other"
          : "received",
        message.isRemoving ? "removing" : "",
        message.wasRecentlyEdited ? "edited-glow" : "",
      ]
        .join(" ")
        .trim();

      nodes.push(
        <div
          id={`msg-${message.id}`}
          key={message.id || message.tempId || Math.random()}
          className={cls}
          onContextMenu={(e) => isMy && handleOpenMenu(message.id, e)}
          onClick={(e) => isMy && handleOpenMenu(message.id, e)}
        >
          <div className="message-sender">{message.sender_name}</div>

          <div className="message-bubble">
            {message.chat_file ? (
              <>
                {message.message && (
                  <div className="message-text">{message.message}</div>
                )}
                <div
                  className="message-file clickable"
                  onClick={() => handleFileDownload(message.chat_file)}
                >
                  <div className="file-icon">
                    <FaPaperclip />
                  </div>
                  <div className="file-details">
                    <div className="file-name">
                      {String(message.chat_file).split("/").pop()}
                    </div>
                    <div className="file-size">
                      {message.file_size
                        ? formatFileSize(message.file_size)
                        : "Unknown size"}
                    </div>
                  </div>
                  <div className="download-icon">
                    <FaFileDownload />
                  </div>
                </div>
              </>
            ) : (
              <div className="message-text">{message.message}</div>
            )}
            <div className="message-meta">
              <span className="timestamp">{formatLocalTime(ts)}</span>

              {message.sender_type === "cso" && (
                <span className="status-icon">
                  {csoMessageRead ? <FaCheckDouble /> : <FaCheck />}
                </span>
              )}

              {message.isOptimistic && (
                <span className="sending-indicator"> • sending...</span>
              )}
              {message.is_edited === 1 && (
                <span className="edited-label"> (edited)</span>
              )}
            </div>
          </div>
        </div>
      );
    });

    return nodes;
  };

  // if (!currentUser) return <div className="loading">Loading...</div>;
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <BarLoader color="#4F46E5" height={2} width={200} />
          <p className="mt-4 text-gray-600">Loading chat...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="cso-chat-container">
      <div className="chat-header">
        <h3>Staff Support Chat</h3>
        <div className="status-info">
          <span>{onlineStaffCount} staff online</span>
          {staffTyping && (
            <span className="typing-indicator">Staff is typing...</span>
          )}
        </div>
      </div>

      <div className="chat-messages" ref={messagesContainerRef}>
        {renderMessagesWithDividers()}
        <div ref={messagesEndRef} />

        {/* Context menu */}
        {openMenuFor &&
          (() => {
            const menuMsg = messages.find((m) => m.id === openMenuFor);
            const isMy =
              menuMsg &&
              String(menuMsg.sender_id) === String(getUserId(currentUser)) &&
              menuMsg.sender_type === "cso";

            if (!isMy) return null;

            return (
              <div
                className="message-context-menu"
                style={{
                  position: "absolute",
                  top: menuPos.top,
                  left: menuPos.left,
                  zIndex: 50,
                }}
              >
                <button
                  className="context-item"
                  onClick={() => {
                    handleStartEdit(menuMsg);
                  }}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  className="context-item danger"
                  onClick={() => {
                    handleDeleteMessage(openMenuFor);
                  }}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            );
          })()}
      </div>

      {/* File preview area (shows when a file is selected) */}
      {file && (
        <div className="file-preview telegram">
          <div className="thumb-wrap fade-in">
            {file.type && file.type.startsWith("image/") && filePreviewUrl ? (
              <img
                src={filePreviewUrl}
                alt={file.name}
                className="file-thumb-img"
              />
            ) : (
              <div className="file-thumb-icon">
                <FaFileAlt />
              </div>
            )}
            <button
              className="file-remove-btn"
              onClick={removeFile}
              title="Remove file"
            >
              <FaTimes />
            </button>
          </div>
          <div className="file-meta">
            <div className="file-name">{file.name}</div>
            <div className="file-size">
              {file.size ? formatFileSize(file.size) : ""}
            </div>
          </div>
        </div>
      )}

      <div className="chat-input">
        <div className="input-actions">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="attach-button"
            title="Attach file"
          >
            <FaPaperclip />
          </button>
        </div>

        {/* <textarea
          ref={messageInputRef}
          placeholder={
            editingMessageId ? "Editing message..." : "Type a message..."
          }
          value={newMessage}
          onChange={(e) => {
            setNewMessage(e.target.value);
            if (e.target.value.trim()) handleTyping(true);
            autoResize();
          }}
          onKeyPress={handleKeyPress}
          rows={1}
          disabled={isSending}
        /> */}
    <textarea
                  ref={messageInputRef}
                  className="message-input"
                  placeholder={
                    editingMessageId
                      ? "Editing message..."
                      : "Type a message..."
                  }
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    // handleTyping();
                    autoResize();
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  rows={1}
                  disabled={isSending}
                />

        <div className="right-actions">
          {editingMessageId && (
            <button
              className="cancel-edit"
              onClick={handleCancelEdit}
              title="Cancel edit"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSendMessage}
            disabled={isSending || (!newMessage.trim() && !file)}
            className="send-button"
            title="Send"
          >
            {isSending ? <div className="spinner"></div> : <MdSend />}
          </button>
        </div>
      </div>

      {error && <div className="chat-error">{error}</div>}
    </div>
  );
};

export default CSOChat;
